import { UserAvatar } from "@v2/components/display/UserAvatar/UserAvatar";
import { IconButton } from "@v2/components/ui/icon-button/IconButton";
import { Textarea } from "@webV2/components/ui/textarea";
import { formatDistanceToNow } from "date-fns";
import { Check, Edit, Trash, X } from "lucide-react";
import { CommentProps } from "./types";

export const Comment = ({
  comment,
  isOwnComment,
  editingCommentId,
  editingCommentText,
  onStartEdit,
  onCancelEdit,
  onEdit,
  onDelete,
  updateCommentLoading,
  onEditingCommentTextChange
}: CommentProps & { onEditingCommentTextChange?: (value: string) => void }) => {
  const isEditing = editingCommentId === comment.id;

  return (
    <div className="group flex space-x-3">
      <UserAvatar
        user={{
          name: comment.user.name,
          email: comment.user.email,
          profile_photo_url: comment.user.profile_photo_url
        }}
        size="sm"
        className="flex-shrink-0"
      />
      <div className="flex-1">
        <div className="mb-2 flex items-center justify-start">
          <div className="flex items-center space-x-2">
            <span className="font-inter font-normal text-gray-900 text-sm">
              {comment.user.name}
            </span>
            <span className="text-gray-500 text-sm">
              {formatDistanceToNow(new Date(comment.created), {
                addSuffix: true
              })}
              {comment.updated && comment.updated !== comment.created && (
                <span className="text-gray-400">
                  {" "}(updated {formatDistanceToNow(new Date(comment.updated), {
                    addSuffix: true
                  })})
                </span>
              )}
            </span>
            {isOwnComment && (
              <div className="flex items-center space-x-1">
                {isEditing ? (
                  <>
                    <IconButton
                      icon={Check}
                      variant="success"
                      onClick={() => onEdit(comment.id)}
                      disabled={updateCommentLoading}
                      aria-label="Save comment"
                    />
                    <IconButton
                      icon={X}
                      variant="neutral"
                      onClick={onCancelEdit}
                      aria-label="Cancel edit"
                    />
                  </>
                ) : (
                  <>
                    <IconButton
                      icon={Edit}
                      variant="neutral"
                      onClick={() => onStartEdit(comment)}
                      aria-label="Edit comment"
                    />
                    <IconButton
                      icon={Trash}
                      variant="danger"
                      onClick={() => onDelete(comment.id)}
                      aria-label="Delete comment"
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        {isEditing ? (
          <div className="space-y-2">
            <Textarea
              value={editingCommentText}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                onEditingCommentTextChange?.(e.target.value);
              }}
              className="min-h-[80px] resize-none border-gray-200 focus:border-gray-300 focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
            />
          </div>
        ) : (
          <p className="whitespace-pre-wrap font-normal text-gray-700 text-sm leading-relaxed">
            {comment.comment}
          </p>
        )}
      </div>
    </div>
  );
};
